// components/TradeDetailsModal.jsx - Compact horizontal layout
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { formatCurrency } from '../utils';

const TradeDetailsModal = ({ trade, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (trade && trade.option_name) {
      fetchOrders();
    }
  }, [trade]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let startDate = null;
      let endDate = null;
      
      if (trade.date_opened) {
        startDate = trade.date_opened;
      }
      
      if (trade.date_closed) {
        endDate = trade.date_closed;
      }
      
      const response = await axios.get(`${API_BASE_PATH}/trades`, {
        params: {
          option_name: trade.option_name,
          start_date: startDate,
          end_date: endDate
        }
      });
      
      const sortedOrders = (response.data || [])
        .filter(order => {
          try {
            const date = new Date(order.filled);
            return !isNaN(date.getTime());
          } catch (e) {
            return false;
          }
        })
        .sort((a, b) => new Date(a.filled) - new Date(b.filled));
      
      setOrders(sortedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  if (!trade) return null;

  const totalFees = (trade.buy_fees || 0) + (trade.sell_fees || 0);
  const pnl = (trade.total_sell_price || 0) - (trade.total_buy_price || 0) - totalFees;
  const pnlPercentage = trade.total_buy_price 
    ? ((pnl / trade.total_buy_price) * 100).toFixed(2)
    : 0;

  const isProfitable = pnl >= 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container trade-details-modal-compact" onClick={(e) => e.stopPropagation()}>
        {/* Compact Header */}
        <div className="modal-header" style={{ padding: '16px 24px' }}>
          <h2 className="modal-title" style={{ fontSize: '18px', margin: 0 }}>
            <i className="fas fa-chart-line"></i>
            {trade.option_name || 'N/A'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Compact Body */}
        <div className="modal-body" style={{ padding: '16px 24px' }}>
          {/* Summary Grid - Horizontal */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '12px',
            marginBottom: '16px',
            padding: '12px',
            background: 'var(--bg-tertiary)',
            borderRadius: '8px'
          }}>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '4px' }}>P&L</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: isProfitable ? 'var(--success-color)' : 'var(--danger-color)' }}>
                {formatCurrency(pnl)} ({pnlPercentage}%)
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '4px' }}>AVG BUY / SELL</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                <span style={{ color: 'var(--success-color)' }}>{formatCurrency(trade.avg_buy_price || 0)}</span>
                {' / '}
                <span style={{ color: 'var(--danger-color)' }}>{formatCurrency(trade.avg_sell_price || 0)}</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '4px' }}>CONTRACTS</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                <span style={{ color: 'var(--success-color)' }}>{trade.quantity_bought || 0} bought</span>
                {' / '}
                <span style={{ color: 'var(--danger-color)' }}>{trade.quantity_sold || 0} sold</span>
              </div>
            </div>
          </div>

          {/* Financial Summary - Horizontal */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '8px',
            marginBottom: '16px',
            padding: '10px',
            background: 'white',
            borderRadius: '8px',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '2px' }}>BUY COST</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(trade.total_buy_price || 0)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '2px' }}>SELL VALUE</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(trade.total_sell_price || 0)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '2px' }}>FEES</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--danger-color)' }}>{formatCurrency(totalFees)}</div>
            </div>
            <div style={{ textAlign: 'center', background: isProfitable ? 'var(--success-light)' : 'var(--danger-light)', borderRadius: '6px', padding: '4px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, marginBottom: '2px' }}>NET P&L</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: isProfitable ? 'var(--success-color)' : 'var(--danger-color)' }}>
                {formatCurrency(pnl)}
              </div>
            </div>
          </div>

          {/* Orders Table - Compact */}
          <div>
            <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-list" style={{ fontSize: '12px' }}></i>
              Orders ({orders.length})
            </h3>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="spinner-border text-primary" style={{ width: '24px', height: '24px' }}></div>
              </div>
            ) : orders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                No orders found
              </div>
            ) : (
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                border: '1px solid var(--border-color)',
                borderRadius: '8px'
              }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead style={{ 
                    position: 'sticky', 
                    top: 0, 
                    background: 'var(--bg-tertiary)',
                    zIndex: 1
                  }}>
                    <tr>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)' }}>TIME</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)' }}>TYPE</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)' }}>QTY</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)' }}>PRICE</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)' }}>VALUE</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)' }}>FEES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, index) => {
                      const isBuy = order.buy_sell === 'Buy';
                      const orderValue = (order.quantity || 0) * (order.price || 0) * 100;
                      
                      let formattedTime = '';
                      try {
                        const date = new Date(order.filled);
                        if (!isNaN(date.getTime())) {
                          formattedTime = date.toLocaleString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                        }
                      } catch (e) {
                        formattedTime = 'Invalid';
                      }

                      return (
                        <tr 
                          key={order.id || index}
                          style={{ 
                            background: isBuy ? 'rgba(74, 222, 128, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                            borderBottom: '1px solid var(--border-color)'
                          }}
                        >
                          <td style={{ padding: '6px 10px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {formattedTime}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 600,
                              background: isBuy ? 'var(--success-color)' : 'var(--danger-color)',
                              color: 'white'
                            }}>
                              {order.buy_sell}
                            </span>
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>
                            {order.quantity || 0}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>
                            {formatCurrency(order.price || 0)}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>
                            {formatCurrency(orderValue)}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--danger-color)' }}>
                            {formatCurrency(order.fees || 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Success celebration for profitable trades */}
          {isProfitable && pnl > 100 && (
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: 'linear-gradient(135deg, rgba(74, 222, 128, 0.1) 0%, rgba(251, 176, 59, 0.1) 100%)',
              borderRadius: '8px',
              border: '2px dashed var(--success-color)',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--success-color)'
            }}>
              🎈 {pnl > 500 ? 'Excellent trade!' : 'Nice trade!'} ✨
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ padding: '12px 24px' }}>
          <button className="btn btn-primary w-100" onClick={onClose} style={{ padding: '10px' }}>
            <i className="fas fa-check"></i>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradeDetailsModal;